import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Sun, Shield, AlertTriangle, Inbox, TrendingDown, TrendingUp, MessageSquare, RefreshCw, Bell, Sparkles, Radar, Target, Clock, Landmark } from 'lucide-react';
import axios from 'axios';
import { getAuthHeaders } from '../utils/auth';
import { isNativeApp, nativePushPermissionState, registerNativePush, setupNativePushListeners } from '../utils/nativePush';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const money = (v, d = 0) => v == null ? '—' : `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: d })}`;

const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
};

export default function Today() {
  const [brief, setBrief] = useState(null);
  const [pulse, setPulse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pushState, setPushState] = useState('checking'); // checking | unsupported | off | on | busy
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/brief/executive`, { headers: getAuthHeaders() });
        setBrief(data.brief || data);
      } catch (e) { /* renders empty state */ }
      setLoading(false);
      try {
        const p = await axios.get(`${API}/today/pulse`, { headers: getAuthHeaders() });
        setPulse(p.data);
      } catch (e) { /* pulse optional */ }
    })();
    (async () => {
      if (isNativeApp()) {
        setupNativePushListeners(navigate);
        try {
          const perm = await nativePushPermissionState();
          setPushState(perm === 'granted' ? 'on' : 'off');
        } catch (e) { setPushState('off'); }
        return;
      }
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) { setPushState('unsupported'); return; }
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        setPushState(sub ? 'on' : 'off');
      } catch (e) { setPushState('off'); }
    })();
  }, []);

  const enablePush = async () => {
    setPushState('busy');
    if (isNativeApp()) {
      try {
        const res = await registerNativePush();
        if (res.denied) { toast.error('Notifications blocked — allow them in iPhone Settings → LEE'); setPushState('off'); return; }
        setPushState('on');
        toast.success('Lock-screen alerts enabled on this iPhone');
      } catch (e) {
        toast.error('Could not enable notifications: ' + (e?.message || 'unknown error'));
        setPushState('off');
      }
      return;
    }
    try {
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') { toast.error('Notifications blocked — allow them in Settings for LEE'); setPushState('off'); return; }
      const { data } = await axios.get(`${API}/push/vapid-public-key`, { headers: getAuthHeaders() });
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.public_key),
      });
      await axios.post(`${API}/push/subscribe`, { subscription: sub.toJSON() }, { headers: getAuthHeaders() });
      setPushState('on');
      toast.success('Lock-screen alerts enabled on this device');
    } catch (e) {
      toast.error('Could not enable notifications' + (e?.response?.status === 503 ? ' — push not configured on server' : ''));
      setPushState('off');
    }
  };

  const testPush = async () => {
    try {
      const { data } = await axios.post(`${API}/push/test`, {}, { headers: getAuthHeaders() });
      if (data.sent > 0) toast.success('Test notification sent — check your lock screen');
      else toast.error(`No device received it (sent ${data.sent}, failed ${data.failed || 0})`);
    } catch (e) { toast.error('Test push failed'); }
  };

  if (loading) return (
    <Layout><div className="flex items-center gap-2 text-muted-foreground text-sm p-8" data-testid="today-loading"><RefreshCw className="w-4 h-4 animate-spin" /> Building your morning…</div></Layout>
  );

  const w = brief?.wealth || {};
  const g = brief?.guardian || {};
  const gf = g.floor || {};
  const risks = brief?.risks || [];
  const p1 = risks.find(r => r.type === 'P1');
  const im = (brief?.inbound_mail || {}).counts || {};
  const inboundTotal = (im.quote_applied || 0) + (im.matched_needs_owner || 0) + (im.held_for_review || 0) + (im.quarantined || 0);
  const nw = typeof w.net_worth === 'object' ? w.net_worth?.value : w.net_worth;

  return (
    <Layout>
      <div className="space-y-5 max-w-3xl" data-testid="today-page">
        <div>
          <p className="text-xs uppercase tracking-widest text-muted-foreground font-bold flex items-center gap-2">
            <Sun className="w-4 h-4 text-primary" /> Today · {brief?.day || new Date().toISOString().slice(0, 10)}
          </p>
          <h1 className="text-2xl sm:text-3xl font-bold text-white mt-2" data-testid="today-headline">
            {brief?.headline || 'Your executive brief'}
          </h1>
        </div>

        {pulse?.worked && (
          <div className="glass-card p-4 border border-primary/25 bg-primary/[0.04]" data-testid="today-pulse">
            <p className="text-xs font-bold text-primary uppercase tracking-wider flex items-center gap-2 mb-2">
              <Sparkles className="w-3.5 h-3.5" /> While you were away, LEE…
            </p>
            {(pulse.lines || []).map((l, i) => {
              const Ic = { radar: Radar, target: Target, clock: Clock, mail: Inbox, alert: AlertTriangle, bank: Landmark }[l.icon] || Sparkles;
              return (
                <p key={i} className="magic-in text-sm text-zinc-200 py-1 flex items-start gap-2" style={{ animationDelay: `${i * 120}ms` }} data-testid={`pulse-line-${i}`}>
                  <Ic className="w-4 h-4 text-primary/80 mt-0.5 shrink-0" /> <span>{l.text}</span>
                </p>
              );
            })}
          </div>
        )}

        {p1 && (
          <div className="glass-card p-4 border border-red-500/60 bg-red-500/10" data-testid="today-p1">
            <p className="text-red-400 font-bold text-sm flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> {p1.detail}</p>
          </div>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="glass-card p-4 border border-white/10" data-testid="today-networth">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Net Worth</p>
            <p className="text-lg font-mono text-white mt-1">{money(nw)}</p>
          </div>
          <div className="glass-card p-4 border border-white/10">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Runway</p>
            <p className="text-lg font-mono text-white mt-1">{gf.runway_months != null ? `${gf.runway_months} mo` : '—'}</p>
          </div>
          <div className="glass-card p-4 border border-white/10">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Risk Capital</p>
            <p className="text-lg font-mono text-white mt-1">{money(gf.risk_capital)}</p>
          </div>
          <div className="glass-card p-4 border border-white/10">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Cash Flow</p>
            <p className={`text-lg font-mono mt-1 ${(w.net_cash_flow || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {(w.net_cash_flow || 0) >= 0 ? <TrendingUp className="w-3 h-3 inline mr-1" /> : <TrendingDown className="w-3 h-3 inline mr-1" />}
              {money(Math.abs(w.net_cash_flow || 0))}/mo
            </p>
          </div>
        </div>

        <div className="glass-card p-4 border border-emerald-500/20 bg-emerald-500/5" data-testid="today-floor">
          <p className="text-xs font-bold text-emerald-400 uppercase tracking-wider flex items-center gap-2">
            <Shield className="w-3.5 h-3.5" /> The Floor — reserve {gf.reserve_progress_pct != null ? `${gf.reserve_progress_pct}%` : '—'} {gf.reserve_funded ? 'FUNDED ✓' : ''}
          </p>
          {g.flow_fix?.current_net_flow != null && (
            <p className="text-xs text-zinc-300 font-mono mt-1">
              Flow Fix: target {money(g.flow_fix.target_net_flow_per_month)}/mo · {g.flow_fix.on_target ? 'ON TARGET ✓' : `gap ${money(g.flow_fix.gap_to_target)}/mo`}
            </p>
          )}
          {g.weekly_call && <p className="text-xs text-zinc-400 mt-1">This week: {g.weekly_call}</p>}
        </div>

        {(brief?.priorities || []).length > 0 && (
          <div className="glass-card p-4 border border-white/10" data-testid="today-priorities">
            <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2">Top priorities</p>
            {(brief.priorities || []).slice(0, 5).map((pr, i) => (
              <p key={i} className="text-sm text-zinc-200 py-0.5">· {typeof pr === 'string' ? pr : pr.title || pr.detail}</p>
            ))}
          </div>
        )}

        {(brief?.ventures || []).length > 0 && (
          <button onClick={() => navigate('/inventions')} className="w-full text-left glass-card p-4 border border-purple-500/20 hover:bg-purple-500/5 transition-colors" data-testid="today-ventures">
            <p className="text-xs font-bold text-purple-300 uppercase tracking-wider mb-2">Venture pulse</p>
            {(brief.ventures || []).slice(0, 3).map((v, i) => (
              <p key={i} className="text-sm text-zinc-200 py-0.5">
                · {v.title} — {v.verified_pct != null ? `${v.verified_pct}% verified` : ''}{v.decisions_waiting > 0 ? ` · ${v.decisions_waiting} decision${v.decisions_waiting > 1 ? 's' : ''} waiting on you` : ' · nothing needs you'}
              </p>
            ))}
          </button>
        )}

        {risks.filter(r => r.type !== 'P1').length > 0 && (
          <div className="glass-card p-4 border border-yellow-500/20" data-testid="today-risks">
            <p className="text-xs font-bold text-yellow-400 uppercase tracking-wider mb-2">Watch</p>
            {risks.filter(r => r.type !== 'P1').slice(0, 4).map((r, i) => (
              <p key={i} className="text-xs text-zinc-300 py-0.5">⚠ {r.detail}</p>
            ))}
          </div>
        )}

        {inboundTotal > 0 && (
          <button onClick={() => navigate('/inbox')} className="w-full text-left glass-card p-4 border border-blue-500/20 hover:bg-blue-500/5 transition-colors" data-testid="today-inbound">
            <p className="text-xs font-bold text-blue-400 uppercase tracking-wider flex items-center gap-2">
              <Inbox className="w-3.5 h-3.5" /> Inbound mail — {im.quote_applied || 0} quotes applied · {(im.held_for_review || 0) + (im.matched_needs_owner || 0)} waiting on you
            </p>
          </button>
        )}

        {pushState !== 'checking' && (
          <div className="glass-card p-4 border border-white/10" data-testid="today-notifications">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                <Bell className="w-3.5 h-3.5 text-primary" /> Lock-screen alerts
                {pushState === 'on' && <span className="text-emerald-400 normal-case font-mono">ON — briefs · P1 alarms · quotes</span>}
              </p>
              {pushState === 'unsupported' ? (
                <p className="text-xs text-zinc-400">Install LEE first: Safari → Share → Add to Home Screen, then open the app</p>
              ) : pushState === 'on' ? (
                <button onClick={testPush} data-testid="push-test-btn"
                  className="text-xs px-3 py-1.5 rounded-md border border-white/20 text-white hover:bg-white/5 transition-colors">
                  Send test alert
                </button>
              ) : (
                <button onClick={enablePush} disabled={pushState === 'busy'} data-testid="push-enable-btn"
                  className="text-xs px-3 py-1.5 rounded-md bg-primary text-black font-bold hover:opacity-90 transition-opacity disabled:opacity-50">
                  {pushState === 'busy' ? 'Enabling…' : 'Enable notifications'}
                </button>
              )}
            </div>
          </div>
        )}

        <div className="flex gap-3 pt-1">
          <button onClick={() => navigate('/')} data-testid="today-open-chat"
            className="flex-1 py-3 rounded-lg bg-primary text-black font-bold text-sm flex items-center justify-center gap-2 hover:opacity-90 transition-opacity">
            <MessageSquare className="w-4 h-4" /> Talk to LEE
          </button>
          <button onClick={() => navigate('/wealth')} data-testid="today-open-wealth"
            className="flex-1 py-3 rounded-lg border border-white/20 text-white font-bold text-sm hover:bg-white/5 transition-colors">
            Wealth
          </button>
        </div>
      </div>
    </Layout>
  );
}
